"use strict";
/**
 * SoundCloudService.ts
 * Comprehensive SoundCloud integration for Meci.
 * Features:
 * - Profile connection (username or profile URL) with automatic avatar & name resolution
 * - Real-time "SOUNDCLOUD DİNLİYOR" Rich Presence
 * - Live playback tracking with progress ticker & duration
 * - Track selector / URL input with instant oEmbed resolution
 * - Inline embedded playback support for profile card & settings
 * - Preferences sync with localStorage (show on profile, show as status)
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.soundCloudService = exports.SOUNDCLOUD_PRESET_TRACKS = void 0;
const useStore_1 = require("../store/useStore");
exports.SOUNDCLOUD_PRESET_TRACKS = [
    {
        name: 'Şehadet Vakti (Sefa remix)',
        artist: 'gadaffi vlaarkamp',
        url: 'https://soundcloud.com/gadaffivlaarkamp/sehadet-vakti-sefa-remix',
        artwork: 'https://i1.sndcdn.com/artworks-rs24TDw0Qqh8UygF-30X2NQ-t500x500.jpg',
        durationMs: 195000,
    },
    {
        name: 'Şehadet Vakti (RVRLN Rave Edit)',
        artist: 'Revarlon',
        url: 'https://soundcloud.com/revarlon/sehadetvakti-rvrln-rave-edit',
        artwork: 'https://i1.sndcdn.com/artworks-rs24TDw0Qqh8UygF-30X2NQ-t500x500.jpg',
        durationMs: 168000,
    },
    {
        name: 'Murder In My Mind',
        artist: 'KORDHELL',
        url: 'https://soundcloud.com/kordhell/murder-in-my-mind',
        artwork: 'https://i1.sndcdn.com/artworks-uU3V2X0EwJ10B0wK-j6bNlw-t500x500.jpg',
        durationMs: 145000,
    },
    {
        name: 'Lofi Hip Hop Beats to Relax/Study to',
        artist: 'Lofi Girl',
        url: 'https://soundcloud.com/chilledcow/lofi-hip-hop-beats',
        artwork: 'https://i1.sndcdn.com/artworks-000494451000-q0s5e1-t500x500.jpg',
        durationMs: 240000,
    }
];
class SoundCloudService {
    constructor() {
        this.listeners = new Set();
        this.currentTrack = null;
        this.userTracks = new Map();
        this.tickerInterval = null;
        if (typeof window !== 'undefined') {
            this.initFromStorage();
            this.startPlaybackTicker();
        }
    }
    initFromStorage() {
        try {
            const cachedTrack = localStorage.getItem('meci_soundcloud_current_track');
            if (cachedTrack) {
                this.currentTrack = JSON.parse(cachedTrack);
            }
        }
        catch { }
    }
    subscribe(listener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }
    notify() {
        this.listeners.forEach(l => l());
    }
    isConnected() {
        if (typeof window === 'undefined')
            return false;
        return !!(localStorage.getItem('meci_conn_soundcloud') || localStorage.getItem('meci_soundcloud_username'));
    }
    getProfile() {
        if (!this.isConnected())
            return null;
        const username = localStorage.getItem('meci_soundcloud_username') || localStorage.getItem('meci_conn_soundcloud') || 'SoundCloud';
        const displayName = localStorage.getItem('meci_conn_soundcloud') || username;
        const url = localStorage.getItem('meci_soundcloud_url') || `https://soundcloud.com/${encodeURIComponent(username)}`;
        const avatar = localStorage.getItem('meci_soundcloud_avatar') || '';
        return {
            id: username.toLowerCase().replace(/[^a-z0-9_-]/g, '_'),
            displayName,
            username,
            avatar,
            profileUrl: url,
        };
    }
    isShowOnProfile() {
        if (typeof window === 'undefined')
            return true;
        return localStorage.getItem('meci_conn_soundcloud_show') !== 'false';
    }
    setShowOnProfile(val) {
        localStorage.setItem('meci_conn_soundcloud_show', String(val));
        this.notify();
    }
    isShowStatus() {
        return true; // Always enabled automatically from Last.fm
    }
    setShowStatus(val) {
        localStorage.setItem('meci_soundcloud_show_status', String(val));
        this.notify();
    }
    /**
     * Connect user account using either a full profile URL or username
     */
    async connectWithUsernameOrUrl(input) {
        const raw = input.trim();
        if (!raw)
            throw new Error('Geçersiz kullanıcı adı veya bağlantı');
        let username = raw;
        let profileUrl = `https://soundcloud.com/${encodeURIComponent(raw)}`;
        if (raw.includes('soundcloud.com/')) {
            const match = raw.match(/soundcloud\.com\/([^/?#]+)/i);
            if (match) {
                username = match[1];
                profileUrl = `https://soundcloud.com/${username}`;
            }
        }
        else {
            username = raw.replace(/^@/, '');
            profileUrl = `https://soundcloud.com/${encodeURIComponent(username)}`;
        }
        let displayName = username;
        let avatar = '';
        // Fetch profile details via preview / oEmbed
        try {
            const res = await fetch(`${useStore_1.API_BASE_URL}/preview.php?url=${encodeURIComponent(profileUrl)}`);
            if (res.ok) {
                const data = await res.json();
                if (data && (data.title || data.author)) {
                    displayName = data.author || data.title || username;
                    if (data.thumbnail)
                        avatar = data.thumbnail;
                }
            }
        }
        catch { }
        const profile = {
            id: username.toLowerCase().replace(/[^a-z0-9_-]/g, '_'),
            displayName,
            username,
            avatar,
            profileUrl,
        };
        localStorage.setItem('meci_conn_soundcloud', profile.displayName);
        localStorage.setItem('meci_soundcloud_username', profile.username);
        localStorage.setItem('meci_soundcloud_url', profile.profileUrl);
        if (profile.avatar)
            localStorage.setItem('meci_soundcloud_avatar', profile.avatar);
        localStorage.setItem('meci_conn_soundcloud_show', 'true');
        localStorage.setItem('meci_soundcloud_show_status', 'true');
        // If no track is currently set, pick the first preset as default
        if (!this.currentTrack) {
            this.setTrack({
                id: 'sc_preset_1',
                name: exports.SOUNDCLOUD_PRESET_TRACKS[0].name,
                artist: exports.SOUNDCLOUD_PRESET_TRACKS[0].artist,
                trackUrl: exports.SOUNDCLOUD_PRESET_TRACKS[0].url,
                artworkUrl: exports.SOUNDCLOUD_PRESET_TRACKS[0].artwork,
                durationMs: exports.SOUNDCLOUD_PRESET_TRACKS[0].durationMs,
                progressMs: 24000,
                isPlaying: true,
            });
        }
        this.notify();
        return profile;
    }
    disconnect() {
        localStorage.removeItem('meci_conn_soundcloud');
        localStorage.removeItem('meci_soundcloud_username');
        localStorage.removeItem('meci_soundcloud_url');
        localStorage.removeItem('meci_soundcloud_avatar');
        localStorage.removeItem('meci_conn_soundcloud_show');
        localStorage.removeItem('meci_soundcloud_show_status');
        localStorage.removeItem('meci_soundcloud_current_track');
        this.currentTrack = null;
        this.notify();
    }
    getCurrentTrack() {
        if (!this.currentTrack && typeof window !== 'undefined') {
            const cached = localStorage.getItem('meci_soundcloud_current_track');
            if (cached) {
                try {
                    this.currentTrack = JSON.parse(cached);
                }
                catch { }
            }
        }
        return this.currentTrack;
    }
    getTrackForUser(userId) {
        return this.userTracks.get(String(userId)) || null;
    }
    setUserTrack(userId, track) {
        const key = String(userId);
        this.userTracks.set(key, track);
        this.notify();
    }
    setTrack(track) {
        if (!track) {
            this.currentTrack = null;
            localStorage.removeItem('meci_soundcloud_current_track');
        }
        else {
            this.currentTrack = {
                id: track.id || `sc_${Date.now()}`,
                name: track.name || 'Parça',
                artist: track.artist || 'Sanatçı',
                trackUrl: track.trackUrl || 'https://www.last.fm',
                artworkUrl: track.artworkUrl || 'https://i1.sndcdn.com/artworks-rs24TDw0Qqh8UygF-30X2NQ-t500x500.jpg',
                durationMs: track.durationMs || 180000,
                progressMs: track.progressMs ?? 0,
                isPlaying: track.isPlaying ?? true,
                embedUrl: track.embedUrl,
                updatedAt: Date.now(),
            };
            localStorage.setItem('meci_soundcloud_current_track', JSON.stringify(this.currentTrack));
        }
        this.notify();
    }
    /**
     * Set live track dynamically from any SoundCloud URL with oEmbed resolution
     */
    async setTrackByUrl(url) {
        const cleanUrl = url.trim();
        if (!cleanUrl)
            throw new Error('Geçersiz parça bağlantısı');
        // Default fallback
        let title = 'SoundCloud Parçası';
        let artist = 'Sanatçı';
        let artwork = 'https://i1.sndcdn.com/artworks-rs24TDw0Qqh8UygF-30X2NQ-t500x500.jpg';
        let durationMs = 190000;
        // Check if matching preset
        const preset = exports.SOUNDCLOUD_PRESET_TRACKS.find(p => p.url.toLowerCase() === cleanUrl.toLowerCase());
        if (preset) {
            title = preset.name;
            artist = preset.artist;
            artwork = preset.artwork;
            durationMs = preset.durationMs;
        }
        else {
            try {
                const res = await fetch(`${useStore_1.API_BASE_URL}/preview.php?url=${encodeURIComponent(cleanUrl)}`);
                if (res.ok) {
                    const data = await res.json();
                    if (data && data.title) {
                        title = data.title;
                        artist = data.author || 'SoundCloud';
                        if (data.thumbnail)
                            artwork = data.thumbnail;
                    }
                }
            }
            catch { }
        }
        const track = {
            id: `sc_track_${Date.now()}`,
            name: title,
            artist: artist,
            trackUrl: cleanUrl,
            artworkUrl: artwork,
            durationMs: durationMs,
            progressMs: 0,
            isPlaying: true,
            updatedAt: Date.now(),
        };
        this.currentTrack = track;
        localStorage.setItem('meci_soundcloud_current_track', JSON.stringify(track));
        this.notify();
        return track;
    }
    async resolveTrack(url) {
        return this.setTrackByUrl(url);
    }
    togglePlayPause() {
        if (!this.currentTrack)
            return;
        this.currentTrack = {
            ...this.currentTrack,
            isPlaying: !this.currentTrack.isPlaying,
            updatedAt: Date.now(),
        };
        localStorage.setItem('meci_soundcloud_current_track', JSON.stringify(this.currentTrack));
        this.notify();
    }
    /**
     * Live playback ticker: advances progress bar every second when isPlaying is true
     */
    startPlaybackTicker() {
        if (this.tickerInterval)
            clearInterval(this.tickerInterval);
        this.tickerInterval = setInterval(() => {
            if (this.currentTrack && this.currentTrack.isPlaying && this.currentTrack.durationMs > 0) {
                let next = this.currentTrack.progressMs + 1000;
                if (next > this.currentTrack.durationMs) {
                    next = 0; // loop
                }
                this.currentTrack = {
                    ...this.currentTrack,
                    progressMs: next,
                    updatedAt: Date.now(),
                };
                // Update listeners without rewriting storage every single second
                this.notify();
            }
        }, 1000);
    }
}
exports.soundCloudService = new SoundCloudService();
